import { useEffect, useMemo, useState } from 'react';
import { MessagesSquare } from 'lucide-react';
import { familyConversationsApi, familyTransport } from '../api/conversations';
import { canSpeak, type ConversationDetail } from '../api/conversations';
import { useConversation } from '../hooks/useConversation';
import { useUnreadAnchor } from '../hooks/useUnreadAnchor';
import { AppBar, Footer, Gutter, Page } from '../ui/Chrome';
import { EmptyState } from '../ui/EmptyState';
import { MessageBubble } from '../ui/MessageBubble';
import { MessageActions } from './MessageActions';
import { familyModeration } from '../api/moderation';
import type { Message } from '../api/client';
import { AnchorLink } from '../ui/AnchorLink';
import { ParticipantStrip } from '../ui/ParticipantStrip';
import { Composer } from './Composer';
import { messageRuns } from '../lib/messageRuns';
import type { Me } from '../api/auth';

/**
 * A thread, read by a parent.
 *
 * The same thread a member sees on {@link LiveThreadScreen}, fetched through `/family` because a
 * guardian holds no membership and every `/units/` route would 404 them. Two things differ, and
 * both are deliberate:
 *
 * **Whether a composer renders depends on the viewer's basis, not on the door they came through.**
 * The original reasoning here — *being able to reply is what being a participant means* — is
 * exactly the sentence TROOP-58 acted on, and its conclusion flipped when a parent stopped being a
 * reader of a roster-scoped thread and became a participant of it. So on the unit thread and every
 * event thread there is a composer; on a Direct thread read under I4 there is none, and no
 * greyed-out one either, because a disabled control would owe an explanation. The screen names who
 * does speak there instead.
 *
 * The participant strip is the **same component** the member's screen renders, disclosure line and
 * all. There is no parent-shaped view of a conversation: the reader sees exactly what everyone in
 * it sees, and everyone in it has been told they are being read. That symmetry is rule 7, and
 * sharing the component rather than copying it is what keeps it true.
 */
export function FamilyThreadScreen({
  conversationId,
  me,
  meId,
  onBack,
  onNavigate,
  onOpenAnchor,
}: {
  conversationId: string;
  /** The signed-in person, for the account control the bar now carries (TROOP-122). */
  me: Me;
  meId: string;
  onBack: () => void;
  /** For the account control. Separate from `onOpenAnchor`, which means one specific link. */
  onNavigate: (path: string) => void;
  /** Where the parent's copy of the anchored event lives. See {@link LiveThreadScreen}. */
  onOpenAnchor?: (path: string) => void;
}) {
  const [detail, setDetail] = useState<ConversationDetail | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  // The message whose action sheet is open, or null. One at a time: `Overlay` traps focus, and
  // two of them would fight over it.
  const [actingOn, setActingOn] = useState<Message | null>(null);

  const { messages, status, error, send, reconcile, markRead } = useConversation(
    familyTransport,
    conversationId,
  );

  // The server is the judge -- it refuses a Guardian-basis post with a 403 either way. This only
  // decides whether the control is offered, so a wrong answer here is cosmetic rather than a hole.
  const mayReply = canSpeak(detail?.yourBasis);

  // Where this thread opens. Captured from the detail before `markRead` moves the marker,
  // which is the whole ordering this depends on (TROOP-99).
  const { firstUnreadId, firstUnread, bottom } = useUnreadAnchor(
    conversationId,
    messages,
    detail?.yourLastReadSeq,
    meId,
  );

  // Who gets a name and who gets a timestamp. Depends on the unread divider as well as on the
  // messages, because a separator ends both runs.
  const runs = useMemo(() => messageRuns(messages, firstUnreadId), [messages, firstUnreadId]);

  useEffect(() => {
    familyConversationsApi
      .detail(conversationId)
      .then(setDetail)
      .catch(() => setLoadError('Could not load this conversation.'));
  }, [conversationId]);

  // Marks read only while the thread is genuinely in front of somebody. A parent gets a real read
  // marker like anyone else -- seeding one on first open would mark messages read that they have
  // not been shown, which is the wrong direction for the one reader whose purpose is oversight.
  useEffect(() => {
    if (messages.length === 0) {
      return;
    }

    const mark = () => {
      if (document.visibilityState === 'visible') {
        void markRead();
      }
    };

    mark();
    document.addEventListener('visibilitychange', mark);
    return () => document.removeEventListener('visibilitychange', mark);
  }, [messages, markRead]);

  if (loadError) {
    return (
      <Page>
        <Gutter>
          <p
            className="t-body"
            style={{ color: 'var(--color-text-secondary)', padding: 'var(--space-6) 0' }}
          >
            {loadError}
          </p>
        </Gutter>
      </Page>
    );
  }

  return (
    <Page>
      <AppBar
        parentLabel="Your family"
        title={detail?.title ?? 'Conversation'}
        subtitle={detail ? `${detail.participants.length} people` : undefined}
        onBack={onBack}
        me={me}
        onNavigate={onNavigate}
      />

      <section className="rule-canonical" style={{ background: 'var(--color-surface-page)' }}>
        <Gutter>
          <ParticipantStrip detail={detail} />
          {/* The same component the member's screen renders, pointed at the parent's own copy of
              the event. An announcement has no detail screen -- the card IS the record -- so it
              lands on /family, where a parent's noticeboard is. */}
          <AnchorLink
            detail={detail}
            onOpen={(kind, id) =>
              onOpenAnchor?.(kind === 'Event' ? `/family/events/${id}` : '/family')
            }
          />
        </Gutter>
      </section>

      <section
        className="flex-1"
        style={{ background: 'var(--color-surface-sunken)', paddingBottom: 'var(--space-6)' }}
      >
        <Gutter>
          <div
            className="flex items-center justify-between gap-4"
            style={{ paddingBlock: 'var(--space-4)' }}
          >
            <h2 className="t-label" style={{ color: 'var(--color-text-muted)', margin: 0 }}>
              Discussion &middot; <span className="tabular">{messages.length}</span>
            </h2>
            <span className="t-caption" style={{ color: 'var(--color-text-secondary)' }}>
              {status === 'connected' ? 'Live' : status === 'reconnecting' ? 'Reconnecting' : 'Offline'}
            </span>
          </div>

          {messages.length === 0 ? (
            <EmptyState
              icon={MessagesSquare}
              headline="Nothing said yet"
              explanation="When anyone posts here, it will appear on this page."
            />
          ) : (
            <div>
              {messages.map((message, i) => (
                <div
                  key={message.id}
                  ref={message.id === firstUnreadId ? firstUnread : undefined}
                >
                  {/* A line rather than a jump. The reader has to be able to see where they left
                      off, or landing in the middle of a thread reads as the page being lost. */}
                  {message.id === firstUnreadId && messages[0]?.id !== message.id && (
                    <div
                      className="flex items-center gap-2"
                      style={{ paddingBlock: 'var(--space-3)' }}
                    >
                      <span
                        aria-hidden="true"
                        style={{ height: 1, flex: 1, background: 'var(--color-accent-announcement)' }}
                      />
                      <span
                        className="t-caption"
                        style={{ color: 'var(--color-accent-announcement)', flexShrink: 0 }}
                      >
                        New
                      </span>
                      <span
                        aria-hidden="true"
                        style={{ height: 1, flex: 1, background: 'var(--color-accent-announcement)' }}
                      />
                    </div>
                  )}

                  <MessageBubble
                    message={{
                      id: message.id,
                      authorName: message.authorDisplayName,
                      authorClassification:
                        detail?.participants.find((p) => p.personId === message.authorPersonId)
                          ?.classification === 'Youth'
                          ? 'youth'
                          : 'adult',
                      body: message.isRemoved ? '' : (message.body ?? ''),
                      time: runs[i].time,
                      // A parent is normally not in the thread at all, so nothing is "theirs". When
                      // they have been added as a participant, their own messages read as their own.
                      own: message.authorPersonId === meId,
                      startsRun: runs[i].startsRun,
                      removed: message.isRemoved,
                      removalBasis: message.removalBasis,
                    }}
                    // The same sheet the member's thread carries (TROOP-12). A parent reading their
                    // child's thread and seeing something wrong in it is one of the two people
                    // section 5 was written for -- a family door that could only watch would be the
                    // read-only annex rule 13b refuses.
                    onOpenActions={() => setActingOn(message)}
                  />
                </div>
              ))}

              {/* Where a caught-up reader lands. An element rather than a scroll height, because
                  the composer sits below this and a height would land above it. */}
              <div ref={bottom} />
            </div>
          )}

          {error && (
            <p
              className="t-caption"
              role="alert"
              style={{ color: 'var(--color-status-blocked)', marginTop: 'var(--space-3)' }}
            >
              {error}{' '}
              <button type="button" className="link-button" onClick={() => void reconcile()}>
                Retry
              </button>
            </p>
          )}

          {mayReply ? (
            <div style={{ marginTop: 'var(--space-5)' }}>
              <Composer onSend={send} />
            </div>
          ) : (
            /* Not a disabled composer: a control that cannot be pressed owes an explanation, and
               naming who does speak here is more use than the control would be. This is the
               honest description of an I4 read -- entitled to look in, never to join in. */
            <p
              className="t-caption"
              style={{
                color: 'var(--color-text-secondary)',
                margin: 'var(--space-5) 0 0',
                maxWidth: '52ch',
              }}
            >
              You can read this because you are a parent of a young person in it. Replying is for
              the people in the thread — if you need to be one of them, ask a leader in the unit to
              add you.
            </p>
          )}

          {/* Reporting is not gated on `mayReply`, deliberately. A parent who may only look in is
              still exactly the person section 5 wants able to raise a concern about what they are
              looking at -- reading is the precondition, speaking is not. */}
          {actingOn && (
            <MessageActions
              message={actingOn}
              transport={familyModeration}
              onClose={() => setActingOn(null)}
              onChanged={() => void reconcile()}
            />
          )}
        </Gutter>
      </section>

      <Footer />
    </Page>
  );
}
